import React, { useMemo, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { useRouter } from "../../../lib/hooks/useRouter";
import { tooltips } from "../../../lib/utils/tooltips";
import { QueryAbortContext } from "../../../lib/context/QueryAbort/QueryAbortContext";

import { PageHeader } from "../../shared/PageHeader/PageHeader";
import { ResourceNavigation } from "../../shared/ResourceNavigation/ResourceNavigation";
import { SubRouteProps } from "../../../lib/routes/routes";
import { InsightsHeaderButtons } from "../shared/InsightsHeaderButtons/InsightsHeaderButtons";
import { DeskValueInsights } from "./Value/DeskValueInsights";
import { DeskUtilization } from "./Utilization/DeskUtilization";
import { DeskRelease } from "./DeskRelease/DeskRelease";
import { InsightsContextProvider } from "../Context/InsightsContextProvider";
import { DateRangeContextProvider } from "../Context/DateRangeContext/DateRangeContextProvider";
import { InsightsForInfo } from "../shared/InsightsForInfo/InsightsForInfo";
import { TimeZoneLabel } from "../shared/TimeZoneLabel/TimeZoneLabel";
import { OverviewContextProvider } from "../Context/Desk/Overview/OverviewContextProvider";
import { UtilizationContextProvider } from "../Context/Desk/Utilization/UtilizationContextProvider";
import { BehaviourContextProvider } from "../Context/Desk/Behaviour/BehaviourContextProvider";
import { DeskHistory } from "./DeskHistory/DeskHistory";
import { DeskInsightsLoaderModal } from "../shared/InsightsLoaderModal/DeskInsightsLoaderModal";
import { InsightsReportCancelled } from "../shared/InsightsReportCancelled/InsightsReportCancelled";

export const DeskInsights = () => {
  const { match, pathname } = useRouter();

  const [cancelled, setCancelled] = useState(false);
  // Bumping the key remounts the data providers, which re-runs all requests.
  const [runKey, setRunKey] = useState(0);

  // One AbortController per run; aborting it cancels the in-flight requests.
  const abortController = useMemo(() => new AbortController(), [runKey]);

  const handleCancel = () => {
    abortController.abort();
    setCancelled(true);
  };

  const handleRunAgain = () => {
    setCancelled(false);
    setRunKey((key) => key + 1);
  };

  const routes: SubRouteProps[] = [
    {
      exact: true,
      label: "Overview",
      to: match.url,
      id: "desk-booking-insights-page",
      component: DeskValueInsights,
    },
    {
      exact: true,
      label: "Utilization",
      to: `${match.url}/utilization`,
      id: "desk-utilization-page",
      component: DeskUtilization,
    },
    {
      exact: true,
      label: "Behaviour",
      to: `${match.url}/behaviour`,
      id: "desk-behaviour-page",
      component: DeskRelease,
    },
    {
      exact: true,
      label: "History",
      to: `${match.url}/history`,
      id: "desk-history-page",
      component: DeskHistory,
    },
  ];

  return (
    <DateRangeContextProvider>
      <InsightsContextProvider>
        <QueryAbortContext.Provider value={abortController.signal}>
          <OverviewContextProvider key={runKey}>
            <UtilizationContextProvider>
              <BehaviourContextProvider>
                {!cancelled && (
                  <DeskInsightsLoaderModal onCancel={handleCancel} />
                )}
                <div className="WorkplaceInsights default-page">
                  <div className="PeopleHeader position-relative">
                    <PageHeader
                      title="Desk Insights Dashboard"
                      hideBorder
                      message={tooltips.insights.desks.heading}
                    >
                      <TimeZoneLabel />
                    </PageHeader>

                    <ResourceNavigation routes={routes}>
                      <InsightsHeaderButtons pathname={pathname} />
                    </ResourceNavigation>
                  </div>

                  <div className="position-relative">
                    {cancelled ? (
                      <InsightsReportCancelled onRunAgain={handleRunAgain} />
                    ) : (
                      <>
                        <InsightsForInfo />

                        <Switch>
                          {routes.map((item) => (
                            <Route
                              key={item.id}
                              path={item.to}
                              component={item.component}
                              exact={item.exact ? true : undefined}
                            />
                          ))}
                          <Redirect to={routes[0].to} />
                        </Switch>
                      </>
                    )}
                  </div>
                </div>
              </BehaviourContextProvider>
            </UtilizationContextProvider>
          </OverviewContextProvider>
        </QueryAbortContext.Provider>
      </InsightsContextProvider>
    </DateRangeContextProvider>
  );
};
